import React, { useEffect, useState } from 'react';
import { View } from 'components/layout/View';
import { Button } from './Button';
import { FormHelperText } from '@material-ui/core';
import Text from 'components/outputs/Text';
import Modal from 'components/layout/Modal';
import ButtonList from 'components/lists/ButtonList';
import Image from 'components/outputs/Image';
import {
  createMobile,
  findExistingMobile,
  getErrorMessage,
  isDuplicateMobileError,
  isSameMobileNumber,
  resendVerification,
  submitOTP,
  toMobileList,
  updateMobile,
} from 'util/rehive';
import { runWithMfa } from 'util/mfaChallenge';
import { useToast } from 'components/contexts/ToastContext';
import MobileInput from './PhoneInput';
import OTPInput from './OTPInput';
import { isEmpty } from 'lodash/fp';
import Status from 'components/outputs/Status';
import { useTranslation } from 'react-i18next';

export default function MobileVerify(props) {
  const { t } = useTranslation();

  let {
    setFieldValue,
    setFieldTouched,
    values,
    error,
    helperText,
    context: { user, company, refreshUser, mobiles, refreshMobiles },
    buttonTopPosition = '50%', // Allow override of vertical position
    buttonRightPosition = 0, // Allow override of horizontal position
    ...restProps
  } = props;
  const { showToast } = useToast();
  const [showModal, setShowModal] = useState(false);
  // Tracks which number the OTP was last sent to, not just that one was sent —
  // otherwise switching to a second number opens the modal without sending.
  const [otpSentFor, setOtpSentFor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingResend, setLoadingResend] = useState(false);
  const [isVerifyTried, setIsVerifyTried] = useState(false);
  const [createdMobile, setCreatedMobile] = useState(null);
  const [customError, setCustomError] = useState(null);
  const [isValidNumber, setIsValidNumber] = useState(false);
  const mobile = values.mobile;
  const isMobileVerified = values.mobile_verification;
  const isEmailVerified = values.email_verification;
  // `user.mobile` only holds the primary number, so fall back to the account's
  // mobile list. Without it a user whose number isn't the primary one is shown
  // an empty field and re-adds a number the account already has.
  const mobileList = toMobileList(mobiles);
  const accountMobile =
    user.mobile ||
    (mobileList.find(item => item.primary) ?? mobileList[0])?.number;
  // Stable baseline for the phone input — only changes when we create a
  // mobile or the user successfully verifies (triggering refreshUser)
  const displayMobile = createdMobile ?? accountMobile;
  // The number the user typed may already be on the account. Creating it again
  // is rejected with a 400, so branch to verification instead of create.
  const existingMobile = findExistingMobile(mobiles, mobile);
  // Verification is sent against the number as the API stores it (E.164), which
  // is not necessarily character-for-character what the user typed.
  const verificationNumber = existingMobile?.number ?? mobile;
  const sentFirstOTP = isSameMobileNumber(mobile, otpSentFor);

  const openModal = async () => {
    setCustomError('');
    const needsCreate =
      mobile &&
      !isSameMobileNumber(mobile, createdMobile) &&
      !isSameMobileNumber(mobile, user.mobile) &&
      !existingMobile;

    if (needsCreate) {
      setLoading(true);
      try {
        // runWithMfa gates the call behind an MFA challenge modal when the
        // platform requires step-up auth, then retries transparently.
        const resp = await runWithMfa(opts =>
          createMobile(
            {
              number: mobile,
              primary: true,
            },
            opts,
          ),
        );

        if (resp?.id && !resp.primary) {
          await runWithMfa(opts =>
            updateMobile(resp.id, { primary: true }, opts),
          );
        }

        setCreatedMobile(mobile);
        setOtpSentFor(mobile);
        setShowModal(true);
        if (typeof refreshMobiles === 'function') await refreshMobiles();
      } catch (e) {
        if (isDuplicateMobileError(e)) {
          setCreatedMobile(mobile);
          setOtpSentFor(mobile);
          setShowModal(true);
        } else if (e?.mfaCancelled) {
          // User dismissed the MFA challenge modal — not an error state.
        } else {
          console.log(e);
          setCustomError(getErrorMessage(e));
        }
      }
      setLoading(false);
    } else if (existingMobile) {
      setLoading(true);
      try {
        // Verification is tracked against the primary number, so promote the
        // record the user is verifying before doing anything else.
        if (!existingMobile.primary && !user?.verification?.mobile) {
          await runWithMfa(opts =>
            updateMobile(existingMobile.id, { primary: true }, opts),
          );
          if (typeof refreshMobiles === 'function') await refreshMobiles();
        }

        if (existingMobile.verified) {
          // Nothing to send — the account already has this number verified.
          setCreatedMobile(mobile);
          setFieldValue('mobile_verification', true);
          if (typeof refreshUser === 'function') await refreshUser();
          showToast({
            variant: 'success',
            text: t('this_number_is_already_verified'),
          });
        } else if (!sentFirstOTP) {
          await resendVerification(
            'mobile',
            { number: verificationNumber },
            company?.id,
          );
          setOtpSentFor(mobile);
          setShowModal(true);
        } else {
          setShowModal(true);
        }
      } catch (e) {
        if (!e?.mfaCancelled) {
          console.log(e);
          setCustomError(getErrorMessage(e));
        }
      }
      setLoading(false);
    } else {
      if ((user.mobile || createdMobile) && !sentFirstOTP) {
        setLoading(true);
        try {
          await resendVerification(
            'mobile',
            { number: verificationNumber },
            company?.id,
          );
          setOtpSentFor(mobile);
          setShowModal(true);
        } catch (e) {
          console.log(e);
          setCustomError(getErrorMessage(e));
        }
        setLoading(false);
      } else {
        setShowModal(true);
      }
    }
  };
  const closeModal = () => {
    setIsVerifyTried(false);
    setShowModal(false);
  };

  useEffect(() => {
    if (!isMobileVerified && user?.verification?.mobile) {
      setFieldValue('mobile_verification', true);
      closeModal();
    }
  }, [user]);

  async function handleResend() {
    setLoadingResend(true);
    try {
      await resendVerification(
        'mobile',
        { number: verificationNumber },
        company?.id,
      );
      showToast({
        variant: 'success',
        text: `Resent: Check your messages on ${mobile} for a verification code`,
      });
    } catch (e) {
      console.log(e);
      const message = getErrorMessage(e);
      if (message) showToast({ variant: 'error', text: message });
    }
    setLoadingResend(false);
  }

  async function handleVerify(otp) {
    setLoading(true);
    try {
      await submitOTP(otp);
      showToast({
        variant: 'success',
        text: 'Phone is verified',
      });
      if (typeof refreshUser === 'function') {
        await refreshUser();
      }
      if (typeof refreshMobiles === 'function') {
        await refreshMobiles();
      }
    } catch (e) {
      console.log(e);
    }
    setIsVerifyTried(true);
    setLoading(false);
  }
  return (
    <>
      {/* <View fD="row" aI="center"> */}
      <View w="100%" style={{ position: 'relative' }}>
        <MobileInput
          setIsValidNumber={setIsValidNumber}
          setFieldTouched={() => setFieldTouched('mobile')}
          onChange={value => setFieldValue('mobile', value)}
          error={Boolean(customError) || error}
          helperText={customError || helperText}
          existing={displayMobile}
          disabled={Boolean(user.verification.mobile)}
        />
        <View
          style={{
            position: 'absolute',
            right: buttonRightPosition,
            top: buttonTopPosition,
            transform: `translateY(-50%)`,
            marginRight: 8
          }}
        >
      {isMobileVerified ? (
          <Status noWrap={false} style={{marginTop: 8, marginRight: 5}} uppercase>{t('verified')}</Status>
      ) : (
        <Button
          disabled={!isValidNumber}
          // disabled={Boolean(customError) || error || !isValidNumber}
          id="Verify"
          color="primary"
          style={{
            color: 'primary',
            borderRadius: 20,
            minWidth: 70,
            maxWidth: 120,
            height: 24,
            fontSize: 16,
            fontWeight: 500,
            padding: '3px 16px',
            marginTop: 8
          }}
          variant={'text'}
          capitalize={false}
          onClick={openModal}
        />
      )}
      </View>
      </View>

      <MobileVerifyModal
        mobile={mobile}
        showModal={showModal}
        closeModal={closeModal}
        loading={loading}
        handleResend={handleResend}
        handleVerify={handleVerify}
        isVerifyTried={isVerifyTried}
        loadingResend={loadingResend}
      />
    </>
  );
}

function MobileVerifyModal(props) {
  const { t } = useTranslation();

  const {
    showModal,
    closeModal,
    mobile,
    handleResend,
    loading,
    handleVerify,
    isVerifyTried,
    loadingResend,
  } = props;
  const [otp, setOtp] = useState();

  const handleOTPComplete = value => setOtp(value);

  const handleOTPVerify = () => {
    if (!otp) return;
    handleVerify(otp);
  };

  return (
    <Modal maxWidth={415} open={showModal} disableBackdropClick>
      <View ph={1} pb={0.5} w={'100%'} aI={'center'} mt={1}>
        <View mb={2} flex={1} aI={'center'}>
          <View mr={2} ml={0}>
            <Image width={100} src="phone" />
          </View>
          <Text s={15} tA="center">
            {t('check_your_messages_on')}{' '}
            <Text c="primary" inline>
              {mobile}
            </Text>{' '}
            {t('for_a_verification_code')}
          </Text>
          <OTPInput length={5} onComplete={handleOTPComplete} />
        </View>
        {isVerifyTried && (
          <Text s={14} c="#FF3939" tA="center" style={{ marginBottom: 8 }}>
            {t('this_code_was_incorrect')}
          </Text>
        )}
        <ButtonList
          layout={'vertical'}
          items={[
            {
              id: 'verify',
              onPress: handleOTPVerify,
              capitalize: true,
              wide: true,
              loading,
            },
            {
              id: 'close',
              variant: 'text',
              onPress: closeModal,
              disabled: loading,
            },
          ]}
        />
        <View fD="row" aI="center">
          <Text s={14} c="#9D9D9D">
            {t('did_not_receive_the_code')}
          </Text>
          <Button
            onClick={handleResend}
            id="resend"
            variant="text"
            color="primary"
            loading={loadingResend}
          />
        </View>
      </View>
    </Modal>
  );
}
